import { inject, Injectable, NgZone, signal } from '@angular/core';

/**
 * Service zum Abfangen und Zählen von Konsolenfehlern.
 * - Alle Fehler werden für Entwickler gezählt (errorCount)
 * - Unbekannte Fehler werden separat für Projektmanager gezählt (unknownErrorCount)
 * - Bekannte Fehler (Angular Dev-Mode, Leaflet/DOM) werden nur für Entwickler angezeigt
 */
@Injectable({
	providedIn: 'root'
})
export class ConsoleErrorService {
	private ngZone = inject(NgZone);

	/** Anzahl aller abgefangenen Fehler (für Entwickler) */
	errorCount = signal<number>(0);

	/** Anzahl unbekannter Fehler (für Projektmanager) */
	unknownErrorCount = signal<number>(0);

	/** Die letzten Fehlermeldungen (max. 50) */
	errors = signal<string[]>([]);

	/** Die letzten unbekannten Fehlermeldungen (max. 50) */
	unknownErrors = signal<string[]>([]);

	private originalConsoleError: typeof console.error;
	private maxErrors = 50;

	constructor() {
		// Original console.error speichern
		this.originalConsoleError = console.error.bind(console);

		// console.error überschreiben
		console.error = (...args: any[]) => {
			this.captureError(args);
			// Original aufrufen
			this.originalConsoleError(...args);
		};

		// Auch unhandled errors abfangen
		window.addEventListener('error', (event) => {
			this.captureError([event.message, event.filename, event.lineno]);
		});

		// Unhandled promise rejections
		window.addEventListener('unhandledrejection', (event) => {
			this.captureError(['Unhandled Promise Rejection:', event.reason]);
		});
	}

	/**
	 * Prüft ob ein Fehler als "bekannt" gilt und nicht für Projektmanager angezeigt werden soll.
	 */
	private isKnownError(message: string): boolean {
		// Angular Dev-Mode Fehler (NG0100: ExpressionChangedAfterItHasBeenCheckedError, etc.)
		if (/NG0[1-9]\d{2}/.test(message)) return true;

		return false;
	}

	private captureError(args: any[]): void {
		const message = args.map(arg => {
			if (arg instanceof Error) {
				return arg.message;
			}
			if (typeof arg === 'object') {
				try {
					return JSON.stringify(arg);
				} catch {
					return String(arg);
				}
			}
			return String(arg);
		}).join(' ');

		// Signal-Updates in NgZone ausführen, um NG0100 zu vermeiden
		this.ngZone.run(() => {
			// Alle Fehler für Entwickler zählen
			this.errorCount.update(count => count + 1);
			this.errors.update(errors => {
				const newErrors = [...errors, message];
				if (newErrors.length > this.maxErrors) {
					return newErrors.slice(-this.maxErrors);
				}
				return newErrors;
			});

			// Unbekannte Fehler separat für Projektmanager zählen
			if (!this.isKnownError(message)) {
				this.unknownErrorCount.update(count => count + 1);
				this.unknownErrors.update(errors => {
					const newErrors = [...errors, message];
					if (newErrors.length > this.maxErrors) {
						return newErrors.slice(-this.maxErrors);
					}
					return newErrors;
				});
			}
		});
	}

	/** Alle Zähler und Fehlerlisten zurücksetzen (für Entwickler) */
	clear(): void {
		this.errorCount.set(0);
		this.errors.set([]);
		this.unknownErrorCount.set(0);
		this.unknownErrors.set([]);
	}

	/** Nur unbekannte Fehler zurücksetzen (für Projektmanager) */
	clearUnknown(): void {
		this.unknownErrorCount.set(0);
		this.unknownErrors.set([]);
	}

	/** HTTP-Fehler erfassen (wird vom HTTP Interceptor aufgerufen) */
	captureHttpError(message: string): void {
		this.captureError([message]);
	}
}
